import { Injectable } from '@angular/core';
import { combineLatest, Observable } from 'rxjs';
import { KoanCardData } from '../../../../../../../models/print-cards/koan-card-data';
import { map, shareReplay } from 'rxjs/operators';
import { PrintCardContentViewModel } from '../../shared/print-card-content-view-model';
import { exists } from '../../../../../../../functions/exists';
import { CannabinoidOrTerpeneFromPool } from '../../shared/card-data-with-property-pooling';
import { RegexUtils } from '../../../../../../../utils/regex-utils';
import { CompoundUtils } from '../../../../../../../utils/compound-utils';

@Injectable()
export class KoanIndexCardStackContentViewModel extends PrintCardContentViewModel {
  public readonly cardData$: Observable<KoanCardData>;

  public readonly koanVisibleTotalTerpenes$ = this.cannabinoidVariantsOnly$(this.helperTotalTerpenes$);

  public readonly koanVisiblePresentTerpenes$ = this.cannabinoidVariantsOnly$(this.helperPresentTerpenes$);

  /**
   * Koan renders total terpenes and present terpenes in a dedicated bottom section, so those admin-aware
   * properties should not consume the max count for the main inline property section.
   */
  public readonly mainProperties$ = combineLatest([
    this.cardData$,
    this.maxNumberOfPropertiesToUseForPropertyBasedPooling$,
    this.helperTAC$,
    this.helperTopTerp$,
    this.allPotentialPropertiesForSecondaryPool$
  ]).pipe(
    map(([cardData, maxProps, TAC, topTerp, secondaryProperties]) => {
      if (cardData?.isNonCannabinoidVariant()) return [];
      const properties = [TAC, topTerp, ...(secondaryProperties ?? [])].filter(prop => exists(prop)).take(maxProps);
      const sortFinalListOfCompoundProperties = this.finalSortLambdaForCompoundProperties();
      if (sortFinalListOfCompoundProperties) properties.sort(sortFinalListOfCompoundProperties);
      return this.applyFinalPropertyValueRules(properties);
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public readonly hasMainProperties$ = this.mainProperties$.pipe(
    map(properties => properties?.length > 0),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public readonly hasTotalTerpenesOrPresentTerpenes$ = combineLatest([
    this.koanVisibleTotalTerpenes$,
    this.koanVisiblePresentTerpenes$
  ]).pipe(
    map(([totalTerpenes, presentTerpenes]) => exists(totalTerpenes) || exists(presentTerpenes)),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public readonly hasTotalTerpenesAndPresentTerpenes$ = combineLatest([
    this.koanVisibleTotalTerpenes$,
    this.koanVisiblePresentTerpenes$
  ]).pipe(
    map(([totalTerpenes, presentTerpenes]) => exists(totalTerpenes) && exists(presentTerpenes)),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public readonly exclusiveOrOfTotalTerpenesAndPresentTerpenes$ = combineLatest([
    this.koanVisibleTotalTerpenes$,
    this.koanVisiblePresentTerpenes$
  ]).pipe(
    map(([totalTerpenes, presentTerpenes]) => {
      const hasTotalTerpenes = exists(totalTerpenes);
      const hasPresentTerpenes = exists(presentTerpenes);
      return hasTotalTerpenes !== hasPresentTerpenes;
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  protected override totalTerpenesPropertyTitle(): string {
    return 'Terpenes';
  }

  private cannabinoidVariantsOnly$<T>(source$: Observable<T>): Observable<T | null> {
    return combineLatest([this.cardData$, source$]).pipe(
      map(([cardData, value]) => (cardData?.isNonCannabinoidVariant() ? null : value)),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  private applyFinalPropertyValueRules(properties: CannabinoidOrTerpeneFromPool[]): CannabinoidOrTerpeneFromPool[] {
    const emptyPropertyValue = this.emptyPropertyValueForPropertiesBasedOnPools();
    if (exists(emptyPropertyValue)) {
      return properties?.map(prop => this.withKoanEmptyPropertyValueIfNeeded(prop, emptyPropertyValue));
    }
    return properties?.filter(prop => !this.koanPropertyNumericValueIsTooSmall(prop));
  }

  private withKoanEmptyPropertyValueIfNeeded(
    prop: CannabinoidOrTerpeneFromPool,
    emptyPropertyValue: string
  ): CannabinoidOrTerpeneFromPool {
    if (!this.propertyAsStringExists(prop?.value) || this.koanPropertyNumericValueIsTooSmall(prop)) {
      return { ...prop, value: emptyPropertyValue };
    }
    return prop;
  }

  private koanPropertyNumericValueIsTooSmall(prop: CannabinoidOrTerpeneFromPool): boolean {
    const numberString = prop?.value?.match(RegexUtils.parseNumbers)?.last();
    const parsedNumericValue = Number.parseFloat(numberString);
    const numericValue: number | null = Number.isNaN(parsedNumericValue) ? null : parsedNumericValue;
    return Number.isFinite(numericValue) && numericValue < CompoundUtils.smallestAllowableValue;
  }
}
